import {
  Button,
  Dropdown,
  Input,
  Menu,
  MenuItem,
  Modal,
  Select,
  Table
} from '@convergence/components';
import {
  DotsThreeVertical,
  Key,
  PlusSquare,
  Users
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAddLabMemberMutation } from '../../api/mutations/addLabMember';
import { useDeleteLabMemberMutation } from '../../api/mutations/deleteLabMember';
import { useUpdateLabMemberMutation } from '../../api/mutations/updateLabMember';
import { useLabMemberQuery } from '../../api/queries/labMember';
import { useLabMembersQuery } from '../../api/queries/labMembers';
import { API_ENDPOINT } from '../../lib/api';
import { useErrorToast } from '../../lib/useErrorToast';
import { HomeStore } from '../../stores/HomeStore';

export interface LabSelectorProps {
  store: HomeStore;
}

export const LabSelector = observer((props: LabSelectorProps) => {
  const queryClient = useQueryClient();
  const [showLabMembers, setShowLabMembers] = useState(false);
  const selectedLab = props.store.selectedLab;

  const labMemberSelfQuery = useLabMemberQuery(selectedLab?.id);
  const canReadLabMembers =
    labMemberSelfQuery.data?.labMember?.role ===
    'OWNER'; /* TODO: un-hardcode this */
  const labMembersQuery = useLabMembersQuery(
    selectedLab?.id,
    canReadLabMembers
  );

  const emailInputRef = useRef<HTMLInputElement>(null);

  const updateLabMemberMutation = useUpdateLabMemberMutation();
  const deleteLabMemberMutation = useDeleteLabMemberMutation();
  const addLabMemberMutation = useAddLabMemberMutation();

  useErrorToast(labMembersQuery.error);
  useErrorToast(updateLabMemberMutation.error);
  useErrorToast(deleteLabMemberMutation.error);
  useErrorToast(addLabMemberMutation.error);

  return (
    <div className="pb-4 ">
      <Dropdown
        className="mb-2"
        label="lab"
        options={
          props.store.labs?.map(({ id, name }) => ({ id, label: name })) ?? []
        }
        onSelect={(id: number) => {
          props.store.setSelectedLabId(id);
        }}
      ></Dropdown>
      {selectedLab ? (
        <div className="flex items-center gap-2 justify-between">
          <span className="text-2xl">{selectedLab.name}</span>
          <Menu button={<DotsThreeVertical size={24} />}>
            {selectedLab.keyPairId && (
              <MenuItem
                onAction={() => {
                  const url = `${API_ENDPOINT}/tinker-boxes/keypair/${selectedLab.keyPairId}`;
                  const el = document.createElement('a');
                  el.href = url;
                  el.download = `${selectedLab.name}.pem`;
                  el.click();
                  el.remove();
                }}
              >
                <Key size={12} />
                Download Key
              </MenuItem>
            )}
            {canReadLabMembers && (
              <MenuItem
                onAction={() => {
                  setShowLabMembers(true);
                }}
              >
                <Users size={12} />
                Lab Members
              </MenuItem>
            )}
          </Menu>
        </div>
      ) : (
        <p className="italic text-4xl">[select a lab]</p>
      )}
      <Modal visible={showLabMembers} onClose={() => setShowLabMembers(false)}>
        <div className="flex flex-col gap-2 h-full overflow-auto">
          <header className="flex items-center justify-between border-b border-black pb-2">
            <h2 className="text-2xl">lab members</h2>
            <div className="flex items-center gap-2">
              <Input ref={emailInputRef} placeholder="email" />
              <button
                className="flex items-center gap-2"
                onClick={() => {
                  if (!selectedLab) {
                    return;
                  }
                  const email = emailInputRef.current?.value;
                  if (email) {
                    addLabMemberMutation.mutate(
                      { email, labId: selectedLab.id },
                      {
                        onSuccess: () => {
                          queryClient.invalidateQueries({
                            queryKey: ['labMembers', selectedLab?.id]
                          });
                        }
                      }
                    );
                  } else {
                    toast.error('Please enter an email address');
                  }
                  emailInputRef.current!.value = '';
                }}
              >
                <PlusSquare size={24} />
              </button>
            </div>
          </header>
          <div className="p-8">
            <Table
              className="w-full"
              defaultSortKey="email"
              renderHeading={(n) => {
                console.log({ n });
                return <tr className="bg-black text-white">{n}</tr>;
              }}
              data={
                labMembersQuery.data?.labMembers
                  .map(({ id, user, role }) => ({
                    'first name': user.firstname,
                    'last name': user.lastname,
                    email: user.email,
                    id,
                    role,
                    delete: ''
                  }))
                  .sort((a, b) => a.email?.localeCompare(b.email ?? '') ?? 0) ??
                []
              }
              hide={{ id: true }}
              render={{
                role: ({ role, id }) => (
                  <Select
                    value={role}
                    onChange={(e) => {
                      updateLabMemberMutation.mutateAsync(
                        { labMemberId: id, role: e.target.value },
                        {
                          onSuccess: async () => {
                            console.log('onSuccess');
                            await queryClient.invalidateQueries({
                              queryKey: ['labMembers', selectedLab?.id]
                            });
                          }
                        }
                      );
                    }}
                  >
                    <option value={'OWNER'}>owner</option>{' '}
                    {/* TODO: un-hardcode this */}
                    <option value={'MEMBER'}>member</option>{' '}
                    {/* TODO: un-hardcode this */}
                  </Select>
                ),
                delete: ({ id }) => (
                  <Button
                    onClick={async () => {
                      deleteLabMemberMutation.mutate(id, {
                        onSettled: async (value, error) => {
                          console.log('onSettled');
                          console.log({ value, error });
                        },
                        onSuccess: async () => {
                          console.log('onSuccess');
                          console.log(
                            `Invalidating labMembers for lab ${selectedLab?.id}`
                          );
                          await queryClient.invalidateQueries({
                            queryKey: ['labMembers', selectedLab?.id]
                          });
                        }
                      });
                    }}
                  >
                    Delete
                  </Button>
                )
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
});
